"use client";

import Link from "next/link";
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useState,
} from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ContactSalesModal } from "~/components/marketing/contact-sales-modal";
import { openBookADemoChat } from "~/lib/siteglue/open-book-a-demo-chat";
import { useSiteGlueMarketingBrand } from "~/components/siteglue/siteglue-marketing-brand";
import {
  getSiteGlueSignupHref,
  getSiteGlueTrishHref,
  SITEGLUE_CTA_CONFIG,
  trackSiteGlueCta,
  trackSiteGlueEvent,
  type SiteGlueCtaAction,
  type SiteGlueCtaLocation,
  type SiteGlueTrishContext,
} from "~/lib/siteglue/cta-config";
import { getMarketingChrome, SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { cn } from "~/lib/utils";

type ButtonVariant = ComponentProps<typeof Button>["variant"];
type ButtonSize = ComponentProps<typeof Button>["size"];

export type SiteGlueCtaVariant = "primary" | "secondary" | "text" | "outline";

type SiteGlueCtaButtonProps = {
  action: SiteGlueCtaAction;
  location: SiteGlueCtaLocation;
  variant?: SiteGlueCtaVariant;
  size?: ButtonSize;
  className?: string;
  showArrow?: boolean;
  /** Override label (rare — prefer config defaults). */
  label?: string;
  /** For launchLiveDemo / contextual demos. */
  href?: string;
  /** Optional Trish context (campaign/referrer only until hire app supports more). */
  trishContext?: SiteGlueTrishContext;
  fullWidth?: boolean;
  testId?: string;
  onBeforeNavigate?: () => void;
  children?: ReactNode;
};

function variantToButtonProps(
  variant: SiteGlueCtaVariant,
): { buttonVariant: ButtonVariant; style?: CSSProperties; className: string } {
  switch (variant) {
    case "primary":
      return {
        buttonVariant: "default",
        style: { backgroundColor: SITEGLUE_COLORS.blue },
        className: "rounded-full text-white hover:opacity-95 focus-visible:ring-[#4f81bd]",
      };
    case "secondary":
      return {
        buttonVariant: "outline",
        className:
          "rounded-full border-slate-300 bg-white text-slate-900 hover:bg-slate-50 focus-visible:ring-[#4f81bd]",
      };
    case "outline":
      return {
        buttonVariant: "outline",
        className:
          "rounded-full border-slate-300 bg-white text-slate-800 hover:bg-slate-50 focus-visible:ring-[#4f81bd]",
      };
    case "text":
      return {
        buttonVariant: "link",
        className:
          "h-auto min-h-11 px-0 text-[#4f81bd] underline-offset-4 hover:underline focus-visible:ring-[#4f81bd]",
      };
  }
}

/**
 * Shared SiteGlue CTA. Prefer action-specific wrappers below for readability.
 */
export function SiteGlueCtaButton({
  action,
  location,
  variant = "primary",
  size = "lg",
  className,
  showArrow = false,
  label: labelOverride,
  href: hrefOverride,
  trishContext,
  fullWidth,
  testId,
  onBeforeNavigate,
  children,
}: SiteGlueCtaButtonProps) {
  const [contactOpen, setContactOpen] = useState(false);
  const config = SITEGLUE_CTA_CONFIG[action];
  const label = labelOverride ?? children ?? config.label;
  const labelText = typeof label === "string" ? label : config.label;
  const { buttonVariant, style, className: variantClass } = variantToButtonProps(variant);

  const track = (destination: string) => {
    trackSiteGlueCta({
      action,
      label: labelText,
      location,
      destination,
      extra:
        action === "trish"
          ? {
              role_name: trishContext?.role,
              industry: trishContext?.industry,
              campaign: trishContext?.campaign,
              referrer: trishContext?.referrer,
            }
          : undefined,
    });
    if (
      (action === "signup" || action === "trish") &&
      (location === "header" ||
        location === "hero" ||
        location === "final_cta" ||
        location === "homepage_trish_section")
    ) {
      trackSiteGlueEvent("homepage_primary_cta_click", { destination, location });
    }
    onBeforeNavigate?.();
  };

  const commonClass = cn(
    "min-h-11 gap-1.5 px-8 text-base font-medium",
    fullWidth && "w-full",
    variantClass,
    className,
  );

  if (action === "bookDemo") {
    return (
      <Button
        type="button"
        size={size}
        variant={buttonVariant}
        style={style}
        className={commonClass}
        data-testid={testId ?? `cta-${action}-${location}`}
        onClick={() => {
          track("book-demo-chat");
          openBookADemoChat();
        }}
      >
        {label}
        {showArrow ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
      </Button>
    );
  }

  if (action === "contactSales") {
    return (
      <>
        <Button
          type="button"
          size={size}
          variant={buttonVariant}
          style={style}
          className={commonClass}
          data-testid={testId ?? `cta-${action}-${location}`}
          onClick={() => {
            track("contact-sales-modal");
            setContactOpen(true);
          }}
        >
          {label}
          {showArrow ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
        </Button>
        <ContactSalesModal open={contactOpen} onOpenChange={setContactOpen} />
      </>
    );
  }

  if (action === "scrollLiveDemo") {
    return (
      <Button
        type="button"
        size={size}
        variant={buttonVariant}
        style={style}
        className={commonClass}
        data-testid={testId ?? `cta-${action}-${location}`}
        onClick={() => {
          const dest = hrefOverride ?? SITEGLUE_CTA_CONFIG.scrollLiveDemo.href;
          track(dest);
          document.getElementById("live-demo")?.scrollIntoView({ behavior: "smooth" });
          window.history.replaceState(null, "", dest);
        }}
      >
        {label}
        {showArrow ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
      </Button>
    );
  }

  if (action === "comparePlans") {
    return (
      <Button
        type="button"
        size={size}
        variant={buttonVariant}
        style={style}
        className={commonClass}
        data-testid={testId ?? `cta-${action}-${location}`}
        onClick={() => {
          const dest = SITEGLUE_CTA_CONFIG.comparePlans.href;
          track(dest);
          document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
          window.history.replaceState(null, "", dest);
        }}
      >
        {label}
        {showArrow ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
      </Button>
    );
  }

  const href =
    hrefOverride ??
    (action === "signup"
      ? getSiteGlueSignupHref()
      : action === "trish"
        ? getSiteGlueTrishHref(trishContext)
        : "href" in config
          ? config.href
          : getSiteGlueSignupHref());

  const isExternal = href.startsWith("http") || href.includes("easygpt.builders");

  return (
    <Button
      asChild
      size={size}
      variant={buttonVariant}
      style={style}
      className={commonClass}
      data-testid={testId ?? `cta-${action}-${location}`}
    >
      <Link
        href={href}
        {...(isExternal
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        onClick={() => track(href)}
      >
        {label}
        {showArrow ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
      </Link>
    </Button>
  );
}

/** Primary conversion CTA */
export function PrimaryCTA(props: Omit<SiteGlueCtaButtonProps, "action">) {
  return (
    <SiteGlueCtaButton
      {...props}
      action="signup"
      variant={props.variant ?? "primary"}
      showArrow={props.showArrow ?? true}
    />
  );
}

/** Scroll to the on-page #live-demo section (industry landing pages). */
export function LiveDemoScrollCTA(
  props: Omit<SiteGlueCtaButtonProps, "action"> & {
    label?: string;
  },
) {
  return (
    <SiteGlueCtaButton
      {...props}
      action="scrollLiveDemo"
      variant={props.variant ?? "secondary"}
      label={props.label ?? SITEGLUE_CTA_CONFIG.scrollLiveDemo.label}
    />
  );
}

/** Product exploration CTA — SiteGlue opens Book a Demo chat; Inbound GPT links to /agents. */
export function DemoCTA(
  props: Omit<SiteGlueCtaButtonProps, "action"> & {
    brandId?: "siteglue" | "inbound_gpt";
  },
) {
  const { brandId, label, ...rest } = props;
  const forcedSiteGlueBrand = useSiteGlueMarketingBrand();
  const isSiteGlueContext = brandId === "siteglue" || forcedSiteGlueBrand === "siteglue";
  const chrome = getMarketingChrome(
    isSiteGlueContext
      ? "www.siteglue.ai"
      : brandId === "inbound_gpt"
        ? "www.easygpt.builders"
        : undefined,
  );
  const isSiteGlue = chrome.brandId === "siteglue";
  return (
    <SiteGlueCtaButton
      action={isSiteGlue ? "bookDemo" : "liveDemo"}
      variant={rest.variant ?? "secondary"}
      label={
        label ??
        (isSiteGlue ? SITEGLUE_CTA_CONFIG.bookDemo.label : SITEGLUE_CTA_CONFIG.liveDemo.label)
      }
      {...rest}
    />
  );
}

/**
 * Personalized onboarding with Trish.
 * Visually secondary to Get Started Free — default outline / text, not primary solid.
 */
export function TrishCTA(props: Omit<SiteGlueCtaButtonProps, "action">) {
  return (
    <SiteGlueCtaButton
      {...props}
      action="trish"
      variant={props.variant ?? "secondary"}
      showArrow={props.showArrow ?? true}
    />
  );
}

/** Guided sales CTA */
export function BookDemoCTA(props: Omit<SiteGlueCtaButtonProps, "action">) {
  return (
    <SiteGlueCtaButton
      action="bookDemo"
      variant={props.variant ?? "secondary"}
      {...props}
    />
  );
}

/** Enterprise / custom sales CTA */
export function ContactSalesCTA(props: Omit<SiteGlueCtaButtonProps, "action">) {
  return (
    <SiteGlueCtaButton
      action="contactSales"
      variant={props.variant ?? "text"}
      {...props}
    />
  );
}

/** Text link CTA with arrow (feature sections, FAQ footnote). */
export function TextLinkCTA({
  action,
  location,
  href,
  label,
  className,
  testId,
}: {
  action: SiteGlueCtaAction;
  location: SiteGlueCtaLocation;
  href?: string;
  label?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <SiteGlueCtaButton
      action={action}
      location={location}
      variant="text"
      size="sm"
      href={href}
      label={label}
      showArrow
      className={cn("text-sm font-semibold", className)}
      testId={testId}
    />
  );
}
